import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear } from 'date-fns';
import {
  Users, Phone, Mail, Building2, Award, TrendingUp, Calendar,
  MapPin, CheckCircle2, XCircle, Clock, MessageSquare, FileText,
  Globe, Share2, Target, DollarSign, Activity, BarChart3,
  PhoneCall, Send, StickyNote, Briefcase, X, Filter, RefreshCw
} from 'lucide-react';

const TIER_CONFIG = {
  bronze: { color: '#CD7F32', label: 'Bronze', rate: '1.5%' },
  silver: { color: '#C0C0C0', label: 'Silver', rate: '2.0%' },
  gold: { color: '#FFD700', label: 'Gold', rate: '2.5%' },
  platinum: { color: '#E5E4E2', label: 'Platinum', rate: '3.0%' }
};

const DATE_PRESETS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'last7days', label: 'Last 7 Days' },
  { value: 'last30days', label: 'Last 30 Days' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'thisQuarter', label: 'This Quarter' },
  { value: 'lastQuarter', label: 'Last Quarter' },
  { value: 'thisYear', label: 'This Year' },
  { value: 'last6Months', label: 'Last 6 Months' },
];

const getDateRangeFromPreset = (preset) => {
  const today = new Date();
  const formatDate = (date) => format(date, 'yyyy-MM-dd');
  
  switch (preset) {
    case 'today':
      return { startDate: formatDate(today), endDate: formatDate(today) };
    case 'last7days':
      return { startDate: formatDate(subDays(today, 7)), endDate: formatDate(today) };
    case 'last30days':
      return { startDate: formatDate(subDays(today, 30)), endDate: formatDate(today) };
    case 'thisMonth':
      return { startDate: formatDate(startOfMonth(today)), endDate: formatDate(today) };
    case 'lastMonth':
      const lastMonth = subMonths(today, 1);
      return { startDate: formatDate(startOfMonth(lastMonth)), endDate: formatDate(endOfMonth(lastMonth)) };
    case 'thisQuarter':
      return { startDate: formatDate(startOfQuarter(today)), endDate: formatDate(today) };
    case 'lastQuarter':
      const lastQuarter = subMonths(today, 3);
      return { startDate: formatDate(startOfQuarter(lastQuarter)), endDate: formatDate(endOfQuarter(lastQuarter)) };
    case 'thisYear':
      return { startDate: formatDate(startOfYear(today)), endDate: formatDate(today) };
    case 'last6Months':
      return { startDate: formatDate(subMonths(today, 6)), endDate: formatDate(today) };
    case 'all':
    default:
      return { startDate: null, endDate: null };
  }
};

const StatCard = ({ icon: Icon, label, value, subValue, color = 'text-indigo-600', bgColor = 'bg-indigo-50' }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${bgColor}`}>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
          {subValue && <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const FunnelBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="font-medium">{value}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all" 
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const ActivityIcon = ({ type }) => {
  const icons = {
    call: PhoneCall,
    whatsapp: MessageSquare,
    email: Mail,
    meeting: Users,
    site_visit: MapPin,
    note: StickyNote
  };
  const Icon = icons[type] || Activity;
  return <Icon className="h-4 w-4" />;
};

export const Partner360View = ({ data, dateRange = {}, onDateRangeChange, onClose }) => {
  const [selectedPreset, setSelectedPreset] = useState('all');
  
  if (!data) return null;

  const { partner, leads, site_visits, conversions, commissions, activities, follow_ups, collateral_shares, microsites, client_tagging, recent_activities, date_range } = data;
  const tierConfig = TIER_CONFIG[partner.tier] || TIER_CONFIG.bronze;

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    const newDateRange = getDateRangeFromPreset(preset);
    if (onDateRangeChange) {
      onDateRangeChange(newDateRange);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    return `₹${amount?.toLocaleString('en-IN') || 0}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMM, hh:mm a');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
            {partner.name?.charAt(0) || 'P'}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold">{partner.name}</h2>
              <Badge 
                style={{ backgroundColor: tierConfig.color + '30', color: tierConfig.color, borderColor: tierConfig.color }}
                className="font-semibold"
              >
                <Award className="h-3 w-3 mr-1" />
                {tierConfig.label} ({tierConfig.rate})
              </Badge>
              {partner.is_approved ? (
                <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Approved
                </Badge>
              ) : (
                <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                  <Clock className="h-3 w-3 mr-1" /> Pending
                </Badge>
              )}
            </div>
            {partner.company_name && (
              <p className="text-muted-foreground flex items-center gap-1 mt-1">
                <Building2 className="h-4 w-4" /> {partner.company_name}
              </p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              {partner.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {partner.phone}
                </span>
              )}
              {partner.email && (
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3" /> {partner.email}
                </span>
              )}
              {partner.rera_number && (
                <span className="flex items-center gap-1">
                  <FileText className="h-3 w-3" /> RERA: {partner.rera_number}
                </span>
              )}
            </div>
            {partner.joined_date && (
              <p className="text-xs text-muted-foreground mt-1">
                <Calendar className="h-3 w-3 inline mr-1" />
                Partner since {formatDate(partner.joined_date)}
              </p>
            )}
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Date Range Filter */}
      <div className="flex items-center justify-between py-3 px-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <Filter className="h-4 w-4 text-indigo-600" />
          </div>
          <span className="text-sm font-semibold text-gray-700">Filter by Date:</span>
          <Select value={selectedPreset} onValueChange={handlePresetChange}>
            <SelectTrigger className="w-[180px] h-9 bg-white border-indigo-200 font-medium" data-testid="date-filter-select">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((preset) => (
                <SelectItem key={preset.value} value={preset.value}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {date_range?.is_filtered && (
          <div className="flex items-center gap-2">
            <Badge className="bg-indigo-100 text-indigo-700 border-indigo-300 text-xs px-3 py-1">
              <Calendar className="h-3 w-3 mr-1" />
              {date_range.start_date} to {date_range.end_date}
            </Badge>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handlePresetChange('all')}
              className="h-8 px-3 text-xs border-indigo-200 hover:bg-indigo-50"
              data-testid="clear-date-filter"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Clear
            </Button>
          </div>
        )}
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard icon={Users} label="Total Leads" value={leads.total} color="text-blue-600" bgColor="bg-blue-50" />
        <StatCard icon={MapPin} label="Site Visits" value={site_visits.completed} subValue={`of ${site_visits.total} scheduled`} color="text-amber-600" bgColor="bg-amber-50" />
        <StatCard icon={CheckCircle2} label="Bookings" value={conversions.total_bookings} color="text-green-600" bgColor="bg-green-50" />
        <StatCard icon={Target} label="Conversion" value={`${conversions.conversion_rate}%`} color="text-purple-600" bgColor="bg-purple-50" />
        <StatCard icon={DollarSign} label="Commission Earned" value={formatCurrency(commissions.total_earned)} color="text-emerald-600" bgColor="bg-emerald-50" />
        <StatCard icon={Activity} label="Activities" value={activities.total} color="text-indigo-600" bgColor="bg-indigo-50" />
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="activities">Activities</TabsTrigger>
          <TabsTrigger value="commissions">Commissions</TabsTrigger>
          <TabsTrigger value="digital">Digital</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Lead Funnel */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" /> Lead Funnel
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <FunnelBar label="New" value={leads.new} total={leads.total} color="#3b82f6" />
                <FunnelBar label="Contacted" value={leads.contacted} total={leads.total} color="#06b6d4" />
                <FunnelBar label="Interested" value={leads.interested} total={leads.total} color="#10b981" />
                <FunnelBar label="Site Visit" value={site_visits.completed} total={leads.total} color="#f59e0b" />
                <FunnelBar label="Negotiation" value={leads.negotiation} total={leads.total} color="#8b5cf6" />
                <FunnelBar label="Booked" value={leads.booked} total={leads.total} color="#22c55e" />
                <FunnelBar label="Lost" value={leads.lost} total={leads.total} color="#ef4444" />
              </CardContent>
            </Card>

            {/* Activity Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-4 w-4" /> Activity Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <PhoneCall className="h-6 w-6 mx-auto text-green-600 mb-1" />
                    <p className="text-2xl font-bold text-green-700">{activities.calls}</p>
                    <p className="text-xs text-green-600">Calls Made</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <MessageSquare className="h-6 w-6 mx-auto text-emerald-600 mb-1" />
                    <p className="text-2xl font-bold text-emerald-700">{activities.whatsapp}</p>
                    <p className="text-xs text-emerald-600">WhatsApp</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <Mail className="h-6 w-6 mx-auto text-blue-600 mb-1" />
                    <p className="text-2xl font-bold text-blue-700">{activities.emails}</p>
                    <p className="text-xs text-blue-600">Emails Sent</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <Briefcase className="h-6 w-6 mx-auto text-purple-600 mb-1" />
                    <p className="text-2xl font-bold text-purple-700">{activities.meetings}</p>
                    <p className="text-xs text-purple-600">Meetings</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Follow-ups Completed</span>
                    <span className="font-medium">{follow_ups.completed} / {follow_ups.total}</span>
                  </div>
                  <Progress value={follow_ups.completion_rate} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity Timeline */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4" /> Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recent_activities?.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No recent activities</p>
              ) : (
                <div className="space-y-3">
                  {recent_activities?.slice(0, 5).map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2 hover:bg-muted/50 rounded-lg">
                      <div className="p-2 bg-muted rounded-lg">
                        <ActivityIcon type={activity.activity_type} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{activity.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {activity.lead_name} • {formatDateTime(activity.created_at)}
                        </p>
                        {activity.outcome && (
                          <p className="text-xs text-muted-foreground mt-1">{activity.outcome}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs capitalize">
                        {activity.activity_type?.replace('_', ' ')}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leads Tab */}
        <TabsContent value="leads" className="space-y-4 mt-4">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Lead Sources */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Lead Sources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(leads.by_source || {}).sort((a, b) => b[1] - a[1]).map(([source, count]) => (
                    <div key={source} className="flex justify-between items-center p-2 bg-muted/50 rounded">
                      <span className="text-sm capitalize">{source.replace('_', ' ')}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                  {Object.keys(leads.by_source || {}).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No source data</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Lead Status Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(leads.by_status || {}).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                    <div key={status} className="flex justify-between items-center p-2 bg-muted/50 rounded">
                      <span className="text-sm capitalize">{status.replace('_', ' ')}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Site Visits */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Site Visits
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm">Total Scheduled</span>
                    <span className="font-medium">{site_visits.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-green-600">Completed</span>
                    <span className="font-medium text-green-600">{site_visits.completed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-amber-600">Scheduled</span>
                    <span className="font-medium text-amber-600">{site_visits.scheduled}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-red-600">Cancelled</span>
                    <span className="font-medium text-red-600">{site_visits.cancelled}</span>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Completion Rate</span>
                      <span className="font-medium">{site_visits.completion_rate}%</span>
                    </div>
                    <Progress value={site_visits.completion_rate} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Trend */}
          {leads.by_month?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" /> Monthly Lead Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2 h-32">
                  {leads.by_month.map((item, idx) => {
                    const maxCount = Math.max(...leads.by_month.map(m => m.count));
                    const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-xs font-medium">{item.count}</span>
                        <div 
                          className="w-full bg-indigo-500 rounded-t transition-all"
                          style={{ height: `${height}%`, minHeight: item.count > 0 ? '8px' : '2px' }}
                        />
                        <span className="text-xs text-muted-foreground">
                          {item.month?.split('-')[1]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Client Tagging */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" /> Client Tagging
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xl font-bold text-blue-700">{client_tagging.total}</p>
                  <p className="text-xs text-blue-600">Total Tagged</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-700">{client_tagging.approved}</p>
                  <p className="text-xs text-green-600">Approved</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xl font-bold text-amber-700">{client_tagging.pending}</p>
                  <p className="text-xs text-amber-600">Pending</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xl font-bold text-red-700">{client_tagging.rejected + client_tagging.expired}</p>
                  <p className="text-xs text-red-600">Rejected/Expired</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activities Tab */}
        <TabsContent value="activities" className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Activity Breakdown */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Activity Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(activities.by_type || {}).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                    <div key={type} className="flex items-center gap-3">
                      <div className="p-2 bg-muted rounded-lg">
                        <ActivityIcon type={type} />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
                          <span className="text-sm font-medium">{count}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ width: `${activities.total > 0 ? (count / activities.total) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Follow-ups */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Follow-ups & Reminders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-xl font-bold text-blue-700">{follow_ups.total}</p>
                      <p className="text-xs text-blue-600">Total</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-xl font-bold text-green-700">{follow_ups.completed}</p>
                      <p className="text-xs text-green-600">Completed</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="text-xl font-bold text-amber-700">{follow_ups.pending}</p>
                      <p className="text-xs text-amber-600">Pending</p>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Completion Rate</span>
                      <span className="font-medium">{follow_ups.completion_rate}%</span>
                    </div>
                    <Progress value={follow_ups.completion_rate} className="h-3" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Full Activity Timeline */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Activity Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {recent_activities?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No activities recorded</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {recent_activities?.map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/30">
                      <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">
                        <ActivityIcon type={activity.activity_type} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{activity.title}</p>
                          <Badge variant="outline" className="text-xs capitalize">
                            {activity.activity_type?.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">Lead: {activity.lead_name}</p>
                        {activity.description && (
                          <p className="text-sm text-muted-foreground mt-1">{activity.description}</p>
                        )}
                        {activity.outcome && (
                          <p className="text-sm mt-1">
                            <span className="text-muted-foreground">Outcome:</span> {activity.outcome}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {formatDateTime(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Commissions Tab */}
        <TabsContent value="commissions" className="space-y-4 mt-4">
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
              <CardContent className="p-4 text-center">
                <DollarSign className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(commissions.total_earned)}</p>
                <p className="text-sm text-emerald-600">Total Earned</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(commissions.pending)}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(commissions.approved)}</p>
                <p className="text-sm text-muted-foreground">Approved</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{formatCurrency(commissions.paid)}</p>
                <p className="text-sm text-muted-foreground">Paid</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Revenue Generated</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-4">
                  <p className="text-3xl font-bold text-indigo-600">{formatCurrency(commissions.total_booking_value)}</p>
                  <p className="text-muted-foreground">Total Booking Value</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    From {commissions.total_deals} deal{commissions.total_deals !== 1 ? 's' : ''}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Conversion Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Lead to Booking</span>
                      <span className="font-medium">{conversions.conversion_rate}%</span>
                    </div>
                    <Progress value={conversions.conversion_rate} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Site Visit to Booking</span>
                      <span className="font-medium">{conversions.site_visit_to_booking_rate}%</span>
                    </div>
                    <Progress value={conversions.site_visit_to_booking_rate} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Digital Tab */}
        <TabsContent value="digital" className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Collateral Shares */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Share2 className="h-4 w-4" /> Collateral Shares
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-4">
                  <p className="text-3xl font-bold text-indigo-600">{collateral_shares.total}</p>
                  <p className="text-muted-foreground">Total Brochures Shared</p>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <MessageSquare className="h-5 w-5 mx-auto text-emerald-600 mb-1" />
                    <p className="text-xl font-bold text-emerald-700">{collateral_shares.whatsapp}</p>
                    <p className="text-xs text-emerald-600">via WhatsApp</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <Mail className="h-5 w-5 mx-auto text-blue-600 mb-1" />
                    <p className="text-xl font-bold text-blue-700">{collateral_shares.email}</p>
                    <p className="text-xs text-blue-600">via Email</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Microsites */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="h-4 w-4" /> Microsites
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-indigo-50 rounded-lg">
                    <p className="text-2xl font-bold text-indigo-700">{microsites.total}</p>
                    <p className="text-xs text-indigo-600">Total Created</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">{microsites.published}</p>
                    <p className="text-xs text-green-600">Published</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-700">{microsites.total_views}</p>
                    <p className="text-xs text-purple-600">Total Views</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-700">{microsites.leads_generated}</p>
                    <p className="text-xs text-amber-600">Leads Generated</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Assigned Projects */}
          {partner.assigned_projects?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" /> Assigned Projects
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-3">
                  {partner.assigned_projects.map((project, idx) => (
                    <div key={idx} className="p-3 border rounded-lg hover:bg-muted/30">
                      <p className="font-medium">{project.name}</p>
                      {project.location && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {project.location}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Partner360View;
